import Splide, { Options } from '@splidejs/splide';
import {stripSplideSlideRoleAttributes} from './splideAccessibility';

const SELECTOR = '.image-slider__splide';
const initialised = new WeakSet<HTMLElement>();

function addKeyActivation(btn: HTMLButtonElement, action: () => void): void {
    btn.addEventListener('click', action);
    btn.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            action();
        }
    });
}

type Variant = 'single' | '2-columns' | '3-columns';

const VARIANT_OPTIONS: Record<Variant, Options> = {
    'single': {
        perPage: 1,
        gap: '0px',
        breakpoints: {},
    },
    '2-columns': {
        perPage: 1,
        gap: '14px',
        breakpoints: {
            768: {
                perPage: 2,
                gap: '24px',
            },
        },
    },
    '3-columns': {
        perPage: 1,
        gap: '14px',
        breakpoints: {
            768: {
                perPage: 2,
                gap: '24px',
            },
            992: {
                perPage: 3,
            },
        },
    },
};

function buildPagination(element: HTMLElement, splide: Splide): void {
    const container = document.createElement('ul');
    container.className = 'splide__pagination';
    element.appendChild(container);

    function getPerPage(): number {
        return splide.options.perPage || 1;
    }

    function rebuild(): void {
        const perPage = getPerPage();
        const pageCount = Math.ceil(splide.length / perPage);
        const currentPage = Math.floor(splide.index / perPage);

        container.innerHTML = '';

        for (let i = 0; i < pageCount; i++) {
            const li = document.createElement('li');
            const btn = document.createElement('button');
            btn.className = 'splide__pagination__page';
            btn.type = 'button';
            btn.setAttribute('aria-label', `Gehe zu Seite ${i + 1}`);
            if (i === currentPage) btn.classList.add('is-active');
            addKeyActivation(btn, () => splide.go(i * (splide.options.perPage || 1)));
            li.appendChild(btn);
            container.appendChild(li);
        }
    }

    function updateActive(index: number): void {
        const currentPage = Math.floor(index / getPerPage());
        container.querySelectorAll<HTMLButtonElement>('.splide__pagination__page').forEach((btn, i) => {
            btn.classList.toggle('is-active', i === currentPage);
        });
    }

    rebuild();
    splide.on('resized', rebuild);
    splide.on('moved', (newIndex: number) => updateActive(newIndex));
}

function initImageSliderElement(element: HTMLElement): void {
    if (initialised.has(element)) return;
    initialised.add(element);

    const variant = (element.dataset.variant as Variant) ?? '3-columns';
    const variantOptions = VARIANT_OPTIONS[variant] ?? VARIANT_OPTIONS['3-columns'];

    const splide = new Splide(element, {
        label: element.dataset.splideLabel || '',
        type: 'slide',
        perMove: 1,
        drag: true,
        touch: true,
        snap: true,
        arrows: false,
        pagination: false,
        keyboard: 'focused',
        mediaQuery: 'min',
        ...variantOptions,
    }).mount();

    stripSplideSlideRoleAttributes(element);

    buildPagination(element, splide);

    const carousel = element.closest('.image-slider__carousel');
    if (!carousel) return;

    const arrowContainer = carousel.querySelector<HTMLElement>('.image-slider__arrows');
    const prevBtn = carousel.querySelector<HTMLButtonElement>('.image-slider__arrow--prev');
    const nextBtn = carousel.querySelector<HTMLButtonElement>('.image-slider__arrow--next');

    if (prevBtn) addKeyActivation(prevBtn, () => splide.go('<'));
    if (nextBtn) addKeyActivation(nextBtn, () => splide.go('>'));

    function getVisibleCount(): number {
        return element.querySelectorAll('.splide__slide:not([aria-hidden="true"])').length || 1;
    }

    function updateArrowVisibility(): void {
        const visible = getVisibleCount();
        const needsNav = splide.length > visible;
        if (arrowContainer) arrowContainer.hidden = !needsNav;
    }

    function updateArrowState(): void {
        const visible = getVisibleCount();
        if (prevBtn) prevBtn.disabled = splide.index == 0;
        if (nextBtn) nextBtn.disabled = splide.index + visible >= splide.length;
    }

    splide.on('mounted move resize', () => {
        updateArrowVisibility();
        updateArrowState();
    });

    const liveRegion = document.createElement('div');
    liveRegion.className = 'visually-hidden';
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    carousel.appendChild(liveRegion);

    splide.on('moved', (newIndex: number) => {
        liveRegion.textContent = `${newIndex + 1} von ${splide.length}`;
    });
}

export function initImageSlider(): void {
    document.querySelectorAll<HTMLElement>(SELECTOR).forEach(initImageSliderElement);

    if (!document.body.classList.contains('neos-backend')) return;

    new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
                if (!(node instanceof HTMLElement)) continue;
                if (node.matches(SELECTOR)) {
                    initImageSliderElement(node);
                }
                node.querySelectorAll<HTMLElement>(SELECTOR).forEach(initImageSliderElement);
            }
        }
    }).observe(document.body, { childList: true, subtree: true });
}
